import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { endpoints, isTerminal } from "./api.js";
import { TASKS_POLL_MS } from "./config.js";
import { useSession } from "./session.jsx";

const WorkspaceContext = createContext(null);

const MAX_TRACKED = 12;
const SEED_INVESTIGATIONS = 5;

/**
 * Shared workspace UI state: the results view (graph/table), the top-bar
 * search, and a lightweight task tracker for running jobs.
 *
 * The contract has no "list running jobs" endpoint, so the tracker seeds
 * itself from the latest few investigations once, then polls only the jobs
 * that are still active (plus any job the user starts or opens).
 */
export function WorkspaceProvider({ children }) {
  const { tenantId, refreshTenant } = useSession();
  const [view, setView] = useState("table");
  const [search, setSearch] = useState("");
  const [tasks, setTasks] = useState({}); // jobId -> { job, investigation }
  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  const trackJob = useCallback((job, investigation) => {
    if (!job?.id) return;
    setTasks((prev) => {
      const cur = prev[job.id];
      const next = { ...prev, [job.id]: { job, investigation: investigation || cur?.investigation || null } };
      const ids = Object.keys(next);
      if (ids.length > MAX_TRACKED) {
        ids
          .sort((a, b) => String(next[a].job.createdAt).localeCompare(String(next[b].job.createdAt)))
          .slice(0, ids.length - MAX_TRACKED)
          .forEach((id) => delete next[id]);
      }
      return next;
    });
  }, []);

  // Seed from recent investigations whenever the tenant changes.
  useEffect(() => {
    setTasks({});
    if (!tenantId) return;
    let alive = true;
    (async () => {
      try {
        const { investigations } = await endpoints.investigations();
        const recent = (investigations || []).slice(0, SEED_INVESTIGATIONS);
        const details = await Promise.allSettled(recent.map((inv) => endpoints.investigation(inv.id)));
        if (!alive) return;
        details.forEach((d) => {
          if (d.status !== "fulfilled") return;
          const { investigation, jobs } = d.value;
          (jobs || []).filter((j) => !isTerminal(j.status)).forEach((j) => trackJob(j, investigation));
        });
      } catch {
        /* the indicator is best-effort */
      }
    })();
    return () => {
      alive = false;
    };
  }, [tenantId, trackJob]);

  // Poll active jobs.
  useEffect(() => {
    const timer = setInterval(async () => {
      const active = Object.values(tasksRef.current).filter((t) => !isTerminal(t.job.status));
      if (!active.length) return;
      let finished = false;
      const results = await Promise.allSettled(active.map((t) => endpoints.job(t.job.id)));
      results.forEach((r) => {
        if (r.status !== "fulfilled") return;
        const job = r.value.job;
        if (isTerminal(job.status)) finished = true;
        trackJob(job);
      });
      if (finished) refreshTenant();
    }, TASKS_POLL_MS);
    return () => clearInterval(timer);
  }, [trackJob, refreshTenant]);

  const taskList = useMemo(
    () =>
      Object.values(tasks).sort((a, b) => String(b.job.createdAt).localeCompare(String(a.job.createdAt))),
    [tasks],
  );
  const runningCount = taskList.filter((t) => !isTerminal(t.job.status)).length;

  const value = useMemo(
    () => ({ view, setView, search, setSearch, tasks: taskList, runningCount, trackJob }),
    [view, search, taskList, runningCount, trackJob],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error("useWorkspace outside WorkspaceProvider");
  return ctx;
}
